import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { api } from '../services/api';

interface SelectSearchSectorProps {
  name: string;
  placeholder?: string;
  error?: string;
  onChange?: (value: string) => void;
  value?: string; // ID do setor pré-selecionado
  costumerId?: string; // ID do cliente para filtrar setores
}

interface Sector {
  id: string;
  name: string;
}

export function SelectSearchSector({
  name,
  placeholder = 'Digite para buscar setor...',
  error,
  onChange,
  value: initialValue,
  costumerId,
}: SelectSearchSectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [filteredSectors, setFilteredSectors] = useState<Sector[]>([]);
  const [selectedSector, setSelectedSector] = useState<Sector | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadSectors = useCallback(async () => {
    if (!costumerId) {
      setSectors([]);
      setFilteredSectors([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/sectors', {
        params: {
          costumer_id: costumerId,
          status: 'A',
        },
      });
      const data = response.data.data || [];
      setSectors(data);
      setFilteredSectors(data);
    } catch (err) {
      setSectors([]);
      setFilteredSectors([]);
    } finally {
      setLoading(false);
    }
  }, [costumerId]);

  // Carregar setores quando costumerId mudar
  useEffect(() => {
    loadSectors();
    // Limpar seleção quando mudar de cliente
    setSelectedSector(null);
    setSearchTerm('');
    setInitialLoaded(false);
  }, [loadSectors, costumerId]);

  // Carregar setor pré-selecionado quando value é passado
  useEffect(() => {
    if (initialValue && sectors.length > 0 && !initialLoaded) {
      const sector = sectors.find(s => s.id === initialValue);
      if (sector) {
        setSelectedSector(sector);
        setSearchTerm(sector.name);
        setInitialLoaded(true);
      }
    }
  }, [initialValue, sectors, initialLoaded]);

  // Filtrar setores localmente baseado no termo de busca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredSectors(sectors);
    } else {
      const filtered = sectors.filter(sector =>
        sector.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredSectors(filtered);
    }
  }, [searchTerm, sectors]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current
        && !dropdownRef.current.contains(event.target as Node)
        && inputRef.current
        && !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.target;
    setSearchTerm(inputValue);

    if (!inputValue) {
      setSelectedSector(null);
      onChange?.('');
    }
  };

  const handleSectorSelect = (sector: Sector) => {
    setSelectedSector(sector);
    setSearchTerm(sector.name);
    setShowDropdown(false);
    onChange?.(sector.id);
  };

  const inputClassName = error
    ? 'h-11 px-4 w-full rounded-lg text-red-500 border-2 border-red-500 bg-white/95 backdrop-blur focus:outline-none focus:ring-0'
    : 'h-11 px-4 w-full rounded-lg text-gray-700 bg-white/95 backdrop-blur border-0 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-sm font-medium';

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        name={name}
        placeholder={!costumerId ? 'Selecione um cliente primeiro' : placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => {
          if (costumerId) {
            setShowDropdown(true);
          }
        }}
        className={inputClassName}
        autoComplete="off"
        disabled={!costumerId}
      />

      {loading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
        </div>
      )}

      {showDropdown && filteredSectors.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredSectors.map((sector) => (
            <button
              type="button"
              key={sector.id}
              onClick={() => handleSectorSelect(sector)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer text-gray-700 border-b border-gray-100 last:border-b-0 text-sm"
            >
              {sector.name}
            </button>
          ))}
        </div>
      )}

      {showDropdown
        && searchTerm.length > 0
        && filteredSectors.length === 0
        && !loading && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          <div className="px-4 py-3 text-gray-500 text-center text-sm">
            Nenhum setor encontrado
          </div>
        </div>
      )}

      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </div>
  );
}
